import { useQuery, type QueryStatus } from "@tanstack/react-query";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { message } from "@/lib/utils";
import { getScoreBarColor } from "@/lib/chart";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";

interface PreferenceItem {
  label: string;
  percentage: number;
}

function PreferenceList({ items }: { items: PreferenceItem[] }) {
  if (!items.length) return <LockedContent message={message} />;

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-2">
          <p className="min-w-0 flex-1 text-sm text-foreground truncate">{item.label}</p>
          <Progress value={Math.round(item.percentage)} className="h-2 w-16 shrink-0 bg-secondary" indicatorClassName={getScoreBarColor(item.percentage)} />
          <p className="w-8 shrink-0 text-right text-sm font-medium text-foreground">{Math.round(item.percentage)}%</p>
        </div>
      ))}
    </div>
  );
}

function PreferencesContent({ status, items }: { status: QueryStatus; items: PreferenceItem[] }) {
  switch (status) {
    case "pending":
      return <PreferencesSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      return <PreferenceList items={items} />;
  }
}

export function ContentPreferences() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchTargetAudience());

  const items = (data?.content_preferences ?? [])
    .filter((item) => item.demand_percentage > 0 && item.content_name)
    .map((item) => ({ label: item.content_name, percentage: item.demand_percentage }));

  return (
    <Card className="relative overflow-hidden flex flex-col h-[280px]">
      <CardHeader className="shrink-0">
        <CardTitle className="text-base font-semibold">Content Preferences</CardTitle>
      </CardHeader>
      <CardContent className="relative pt-0 flex-1 flex flex-col overflow-y-auto scrollbar-hidden">
        <PreferencesContent status={status} items={items} />
      </CardContent>
    </Card>
  );
}

function PreferencesSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-2 flex-1 rounded-full" />
          <Skeleton className="h-4 w-10" />
        </div>
      ))}
    </div>
  );
}
